import { Button, Table, Select, Input, DatePicker } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    fetchTrackingRecordsAction,
    changeTrackingParameter,
    exportTrackingDataAction,
} from '../../redux/slice/tracking-reports.slice';
import { DownloadOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';

const { RangePicker } = DatePicker;
const { Option } = Select;

export default function TrackingReports() {
    const tracking = useSelector((state) => state.tracking_report);
    const dispatch = useDispatch();
    const [filters, setFilters] = useState({
        content_type: '',
        customer_email: '',
        user_plan: '',
        big_ref_no: '',
        keywords: '',
        dateRange: null,
    });
    const [pagination, setPagination] = React.useState({
        current: 1,
        pageSize: 15,
        total: 0,
        sort: { createdAt: -1 },
    });

    useEffect(() => {
        // Set default values if tracking state is not initialized
        const defaultParams = {
            pageNo: 0,
            limit: 15,
            sortBy: -1,
            sortField: 'createdAt',
        };

        setPagination({
            current: (tracking.pageNo || 0) + 1,
            pageSize: tracking.limit || 15,
            total: tracking.count || 0,
            sort: { [`${tracking.sortField || 'createdAt'}`]: tracking.sortBy || -1 },
        });

        const fetchParams = {
            pageNo: tracking.pageNo || 0,
            limit: tracking.limit || 15,
            sortBy: tracking.sortBy || -1,
            sortField: tracking.sortField || 'createdAt',
            content_type: tracking.content_type || '',
            big_ref_no: tracking.big_ref_no || '',
            customer_email: tracking.customer_email || '',
            user_plan: tracking.user_plan || '',
            keywords: tracking.keywords || '',
        };

        // Only add date parameters if they exist in tracking state
        if (tracking.start_date) fetchParams.start_date = tracking.start_date;
        if (tracking.end_date) fetchParams.end_date = tracking.end_date;

        fetchRecord(fetchParams);
    }, []);

    useEffect(() => {
        setPagination({
            ...pagination,
            total: tracking.count,
        });
    }, [tracking.count]);

    function fetchRecord(params) {
        dispatch(fetchTrackingRecordsAction(params));
    }

    const handleExport = async () => {
        const exportParams = {};

        if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
            exportParams.start_date = filters.dateRange[0].format('YYYY-MM-DD');
            exportParams.end_date = filters.dateRange[1].format('YYYY-MM-DD');
        }

        if (filters.content_type) exportParams.content_type = filters.content_type;
        if (filters.customer_email) exportParams.customer_email = filters.customer_email;
        if (filters.user_plan) exportParams.user_plan = filters.user_plan;
        if (filters.big_ref_no) exportParams.big_ref_no = filters.big_ref_no;

        await dispatch(exportTrackingDataAction(exportParams));
    };

    const handleSearch = () => {
        const searchParams = {
            pageNo: 0,
            limit: parseInt(tracking.limit) || 15,
            sortBy: parseInt(tracking.sortBy) || -1,
            sortField: tracking.sortField || 'createdAt',
        };

        if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
            searchParams.start_date = filters.dateRange[0].format('YYYY-MM-DD');
            searchParams.end_date = filters.dateRange[1].format('YYYY-MM-DD');
        }

        if (filters.content_type) searchParams.content_type = filters.content_type;
        if (filters.customer_email) searchParams.customer_email = filters.customer_email;
        if (filters.user_plan) searchParams.user_plan = filters.user_plan;
        if (filters.big_ref_no) searchParams.big_ref_no = filters.big_ref_no;
        if (filters.keywords) searchParams.keywords = filters.keywords;

        dispatch(changeTrackingParameter(searchParams));
        fetchRecord(searchParams);
    };

    const handleReset = () => {
        const resetFilters = {
            content_type: '',
            customer_email: '',
            user_plan: '',
            big_ref_no: '',
            keywords: '',
            dateRange: null,
        };
        setFilters(resetFilters);

        const resetParams = {
            pageNo: 0,
            limit: 15,
            sortBy: -1,
            sortField: 'createdAt',
        };

        dispatch(changeTrackingParameter(resetParams));
        fetchRecord(resetParams);
    };

    const onChange_table = (paginate, filter, sorter, extra) => {
        paginate.total = tracking.count;
        paginate.sort = {};

        if (extra.action == 'sort') {
            paginate.sort[`${sorter.field}`] = sorter.order == 'ascend' ? 1 : -1;
        } else {
            paginate.sort = pagination.sort;
        }
        setPagination(paginate);

        const tableParams = {
            pageNo: paginate.current - 1,
            limit: parseInt(paginate.pageSize) || 15,
            sortBy: parseInt(paginate.sort[Object.keys(paginate.sort)[0]]) || -1,
            sortField: Object.keys(paginate.sort)[0] || 'createdAt',
        };

        if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
            tableParams.start_date = filters.dateRange[0].format('YYYY-MM-DD');
            tableParams.end_date = filters.dateRange[1].format('YYYY-MM-DD');
        }

        if (filters.content_type) tableParams.content_type = filters.content_type;
        if (filters.customer_email) tableParams.customer_email = filters.customer_email;
        if (filters.user_plan) tableParams.user_plan = filters.user_plan;
        if (filters.big_ref_no) tableParams.big_ref_no = filters.big_ref_no;
        if (filters.keywords) tableParams.keywords = filters.keywords;

        dispatch(fetchTrackingRecordsAction(tableParams));
    };

    const columns = [
        {
            title: 'Tracking ID',
            dataIndex: 'tracking_id',
            key: 'tracking_id',
            width: 120,
            fixed: 'left',
        },
        {
            title: 'Content Type',
            dataIndex: 'content_type',
            key: 'content_type',
            width: 120,
            render: (text) => text?.charAt(0).toUpperCase() + text?.slice(1) || 'N/A',
        },
        {
            title: 'BIG Ref No',
            dataIndex: 'big_ref_no',
            key: 'big_ref_no',
            width: 120,
        },
        {
            title: 'Content Title',
            dataIndex: ['content_details', 'title'],
            key: 'content_title',
            width: 200,
            ellipsis: true,
        },
        {
            title: 'Customer Name',
            dataIndex: 'customer_name',
            key: 'customer_name',
            width: 150,
        },
        {
            title: 'Customer Email',
            dataIndex: 'customer_email',
            key: 'customer_email',
            width: 180,
        },
        {
            title: 'User Plan',
            dataIndex: 'user_plan',
            key: 'user_plan',
            width: 100,
            render: (text) => text?.charAt(0).toUpperCase() + text?.slice(1) || 'N/A',
        },
        {
            title: 'Plan Name',
            dataIndex: 'plan_name',
            key: 'plan_name',
            width: 120,
        },
        {
            title: 'View Count',
            dataIndex: 'view_count',
            key: 'view_count',
            width: 100,
            sorter: true,
        },
        {
            title: 'First Viewed',
            dataIndex: 'first_viewed_at',
            key: 'first_viewed_at',
            width: 150,
            render: (text) => (text ? moment(text).format('DD/MM/YYYY HH:mm') : 'N/A'),
            sorter: true,
        },
        {
            title: 'Last Viewed',
            dataIndex: 'last_viewed_at',
            key: 'last_viewed_at',
            width: 150,
            render: (text) => (text ? moment(text).format('DD/MM/YYYY HH:mm') : 'N/A'),
            sorter: true,
        },
        {
            title: 'IP Address',
            dataIndex: 'ip_address',
            key: 'ip_address',
            width: 120,
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (text) => (text ? moment(text).format('DD/MM/YYYY HH:mm') : 'N/A'),
            sorter: true,
        },
    ];

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl">Tracking Reports</h1>
                <div className="flex items-center gap-2">
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={handleExport}
                        className="bg-blue-500"
                    >
                        Export CSV
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Content Type
                        </label>
                        <Select
                            placeholder="Select content type"
                            value={filters.content_type}
                            onChange={(value) =>
                                setFilters({ ...filters, content_type: value })
                            }
                            allowClear
                            className="w-full"
                        >
                            <Option value="tender">Tender</Option>
                            <Option value="grant">Grant</Option>
                            <Option value="contract_award">Contract Award</Option>
                            <Option value="project">Project</Option>
                        </Select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Customer Email
                        </label>
                        <Input
                            placeholder="Enter customer email"
                            value={filters.customer_email}
                            onChange={(e) =>
                                setFilters({ ...filters, customer_email: e.target.value })
                            }
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            User Plan
                        </label>
                        <Select
                            placeholder="Select user plan"
                            value={filters.user_plan}
                            onChange={(value) =>
                                setFilters({ ...filters, user_plan: value })
                            }
                            allowClear
                            className="w-full"
                        >
                            <Option value="free">Free</Option>
                            <Option value="basic">Basic</Option>
                            <Option value="premium">Premium</Option>
                            <Option value="enterprise">Enterprise</Option>
                        </Select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            BIG Ref No
                        </label>
                        <Input
                            placeholder="Enter BIG Ref No"
                            value={filters.big_ref_no}
                            onChange={(e) =>
                                setFilters({ ...filters, big_ref_no: e.target.value })
                            }
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Keywords
                        </label>
                        <Input
                            placeholder="Search keywords"
                            value={filters.keywords}
                            onChange={(e) =>
                                setFilters({ ...filters, keywords: e.target.value })
                            }
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date Range
                        </label>
                        <RangePicker
                            value={filters.dateRange}
                            onChange={(dates) =>
                                setFilters({ ...filters, dateRange: dates })
                            }
                            className="w-full"
                            format="YYYY-MM-DD"
                        />
                    </div>

                    <div className="flex items-end gap-2">
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            className="bg-blue-500"
                        >
                            Search
                        </Button>
                        <Button icon={<ReloadOutlined />} onClick={handleReset}>
                            Reset
                        </Button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm">
                <Table
                    loading={tracking.loading}
                    dataSource={tracking.records}
                    columns={columns}
                    scroll={{ x: 1500, y: 500 }}
                    onChange={onChange_table}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} items`,
                        pageSizeOptions: ['15', '30', '50', '100'],
                    }}
                />
            </div>
        </div>
    );
}
